/**
 * The "AI calls out" form's automation fields (ring R4): the switch that lets
 * the automation work the active campaigns by itself, when it may ring (the
 * calling window, its zone and the public holidays) and the gap between two
 * calls.
 *
 * Its own file because the card already carries the switch, test mode, the
 * numbers, the limits and the opening; these are one more group with one
 * question behind them, "when does a machine ring a stranger". The draft and
 * the patch live in `aiOutboundNotes.ts`; this file only draws them. "Stop
 * everything" and what the automation is doing right now live on the AI calls
 * page's automation tab, not in a settings form that saves on a button.
 *
 * The week editor is the phone line's own (`HoursSchedule`), which allows a
 * window past midnight and a close at 24:00. The server reads both as a closed
 * day for AI calls, so this group says which days that closes before the save
 * switches them off, and the editor's own "night shift" note is not shown: it
 * would say the opposite right above.
 */
import { useT } from "../../../../../i18n/I18nProvider";
import { TimezoneCombobox } from "@wa/ui";
import { OUTBOUND_MIN_GAP_SEC } from "../../../../../lib/aiOutbound";
import { Field, FieldBox, FieldGroup, Note, Pill, ToggleRow } from "../../panel/fields";
import { HoursSchedule } from "../HoursSchedule";
import { WEEK_ORDER } from "../hoursWeek";
import { closedWindowDays, type OutboundDraft } from "./aiOutboundNotes";

export function AiOutboundAutomationFields({
  draft,
  onChange,
}: {
  draft: OutboundDraft;
  onChange: (next: OutboundDraft) => void;
}) {
  const t = useT();

  function set<K extends keyof OutboundDraft>(key: K, value: OutboundDraft[K]) {
    onChange({ ...draft, [key]: value });
  }

  // In screen order (Monday first), the way the week editor lists them.
  const closed = new Set(closedWindowDays(draft.days));
  const closedNames = WEEK_ORDER.filter((weekday) => closed.has(weekday)).map((weekday) => t(`settings.hours.day.${weekday}`));

  return (
    <FieldGroup title={t("settings.aiOutbound.automation.group")}>
      <ToggleRow
        label={t("settings.aiOutbound.automation.autopilot")}
        hint={t("settings.aiOutbound.automation.autopilotHint")}
        checked={draft.autopilot}
        onChange={(autopilot) => set("autopilot", autopilot)}
      />
      {/* Said from the draft: both switches are on this form, and the second one
          does nothing while the first is off. */}
      {draft.autopilot && !draft.enabled ? <Note tone="info">{t("settings.aiOutbound.automation.autopilotNeedsEnable")}</Note> : null}

      {/* `FieldBox`: the week is a column of switches and menus, not one input a
          label could forward its clicks to. */}
      <FieldBox label={t("settings.aiOutbound.automation.window")} hint={t("settings.aiOutbound.automation.windowHint")}>
        <div className="mt-1">
          <HoursSchedule days={draft.days} onChange={(days) => set("days", days)} overnight="closed" />
        </div>
      </FieldBox>
      {closedNames.length > 0 ? (
        <Note tone="warn">{t("settings.aiOutbound.automation.overnightClosed", { days: closedNames.join(", ") })}</Note>
      ) : null}

      <div className="grid gap-x-6 gap-y-4 sm:grid-cols-2">
        <FieldBox label={t("settings.aiOutbound.automation.timezone")} hint={t("settings.aiOutbound.automation.timezoneHint")}>
          <TimezoneCombobox
            className="h-11"
            value={draft.timezone}
            onChange={(timezone) => set("timezone", timezone)}
            aria-label={t("settings.aiOutbound.automation.timezone")}
            searchPlaceholder={t("common.timezoneSearch")}
            emptyLabel={t("common.timezoneNone")}
          />
        </FieldBox>
        <Field
          label={t("settings.aiOutbound.automation.minGapSec")}
          hint={t("settings.aiOutbound.automation.minGapSecHint", {
            min: OUTBOUND_MIN_GAP_SEC.min,
            max: OUTBOUND_MIN_GAP_SEC.max,
            fallback: OUTBOUND_MIN_GAP_SEC.fallback,
          })}
        >
          <Pill
            className="h-10 max-w-[120px]"
            value={draft.minGapSec}
            onChange={(next) => set("minGapSec", next)}
            inputMode="numeric"
            placeholder={String(OUTBOUND_MIN_GAP_SEC.fallback)}
          />
        </Field>
      </div>

      <ToggleRow
        label={t("settings.aiOutbound.automation.skipHolidays")}
        hint={t("settings.aiOutbound.automation.skipHolidaysHint")}
        checked={draft.skipHolidays}
        onChange={(skipHolidays) => set("skipHolidays", skipHolidays)}
      />
    </FieldGroup>
  );
}
